import { useCallback, useEffect, useRef } from "react";
import {
  PnlSnapshotSseEventSchema,
  PnlSnapshotSseEventType,
  requireFullLeaderboard,
  requireFullUserSnapshots,
} from "@cede/types";
import { getLeaderboardQueryKey, Logger } from "@cede/utils";
import { getPnlSnapshotQueryKey, invalidatePnlSnapshot, type PnlSnapshotQueryData } from "./pnl/usePnlSnapshot";
import { getLeaderboardSseEndpoint } from "@/utils/api";
import {
  applyPriceEvent as applyPriceEventReducer,
  applyTradeSnapshot as applyTradeSnapshotReducer,
} from "@/utils/pnl/pnl-snapshot-reducer";
import * as Sentry from "@sentry/nextjs";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { create } from "zustand";

const HUB_FRAME_TYPES: ReadonlySet<string> = new Set([
  PnlSnapshotSseEventType.LEADERBOARD,
  PnlSnapshotSseEventType.PNL_SNAPSHOT,
  PnlSnapshotSseEventType.PRICE_EVENT,
  PnlSnapshotSseEventType.RESYNC,
]);

function isHubFrame(raw: unknown): boolean {
  return typeof raw === "object" && raw !== null && HUB_FRAME_TYPES.has(String((raw as { type?: unknown }).type));
}

type RealtimeLeaderboardStatus = {
  active: boolean;
  setActive: (active: boolean) => void;
};

export const useRealtimeLeaderboardStatus = create<RealtimeLeaderboardStatus>((set) => ({
  active: false,
  setActive: (active: boolean) => set({ active }),
}));

/**
 * Single realtime stream per (campaign, epoch): the leaderboard SSE now carries
 * BOTH leaderboard rankings AND the PnL/price events (merged server-side),
 * so one EventSource feeds both the leaderboard cache and the PnL-snapshot cache
 * — one fewer browser socket (SSE holds HTTP/1.1 sockets open; the cap is
 * ~6/origin). Every frame is a discriminated `{ type, payload }`.
 */
export const useRealtimeLeaderboard = ({
  campaignName,
  epochId,
  exchangeId,
  enable,
}: {
  campaignName: string;
  epochId: number;
  exchangeId?: string;
  enable: boolean;
}) => {
  const activeEventSource = useRef<EventSource | null>(null);
  const queryClient = useQueryClient();
  // Selector (not whole-store destructure) so the hook doesn't re-render on every
  // store change; `setActive` is referentially stable across renders.
  const setActive = useRealtimeLeaderboardStatus((state) => state.setActive);

  const disconnectSse = useCallback(() => {
    if (!activeEventSource.current) return;
    activeEventSource.current.close();
    activeEventSource.current = null;
    setActive(false);
    Logger.log("Closing campaign stream", Logger.types.INFO);
  }, [setActive]);

  const connectSse = useCallback(() => {
    if (!campaignName || epochId == null || !enable) return;
    disconnectSse();
    const eventSource = new EventSource(getLeaderboardSseEndpoint(campaignName, String(epochId)));
    const pnlQueryKey = getPnlSnapshotQueryKey(campaignName, epochId, exchangeId);

    eventSource.addEventListener("open", () => {
      Logger.log("Realtime campaign stream connected", Logger.types.INFO);
      setActive(true);
    });

    eventSource.addEventListener("message", (event) => {
      try {
        const raw: unknown = JSON.parse(event.data);
        // Arena-only frames are not Hub's contract: skip them before parsing so their schema can evolve freely.
        if (!isHubFrame(raw)) return;
        const data = PnlSnapshotSseEventSchema.parse(raw);

        // The merged stream multiplexes leaderboard + PnL/price frames;
        // discriminate here and hand each already-narrowed variant to its writer.
        switch (data.type) {
          case PnlSnapshotSseEventType.LEADERBOARD: {
            // Hub campaigns are never Arena, so their records always carry volumes.
            const leaderboard = requireFullLeaderboard(data.payload.leaderboard);
            // useLeaderboard appends an `authenticated` boolean to the base key, so
            // write every variant (with and without the suffix).
            const baseKey = getLeaderboardQueryKey(leaderboard.campaign, leaderboard.epochId);
            queryClient.setQueryData(baseKey, leaderboard);
            queryClient.setQueryData([...baseKey, true], leaderboard);
            queryClient.setQueryData([...baseKey, false], leaderboard);
            break;
          }
          case PnlSnapshotSseEventType.PNL_SNAPSHOT:
            applyPnlSnapshot(queryClient, data, pnlQueryKey, { campaignName, epochId, exchangeId });
            break;
          case PnlSnapshotSseEventType.PRICE_EVENT:
            applyPriceEvent(queryClient, data, pnlQueryKey);
            break;
          // The server resubscribed a feed after an error; frames may be lost, so re-read the PnL state.
          case PnlSnapshotSseEventType.RESYNC:
            if (data.payload.feed === "pnl" || data.payload.feed === "market-price") {
              invalidatePnlSnapshot(queryClient, { campaignName, epochId, exchangeId });
            }
            break;
        }
      } catch (error) {
        Sentry.captureException(error instanceof Error ? error : new Error("Failed to parse campaign stream event"), {
          extra: { campaignName, epochId, exchangeId, error: error instanceof Error ? error.message : "Unknown error" },
        });
      }
    });
    activeEventSource.current = eventSource;
  }, [campaignName, epochId, exchangeId, enable, queryClient, setActive, disconnectSse]);

  // Init the event source
  useEffect(() => {
    connectSse();
    return () => {
      disconnectSse();
    };
  }, [connectSse, disconnectSse]);

  // Disconnect the event source when the user unfocuses the window
  useEffect(() => {
    if (!enable) return;
    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectSse();
      } else {
        connectSse();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enable, connectSse, disconnectSse]);
};

// The reducers are pure, so re-running them against the cached hydration state
// is safe — the cache is the single source of truth. Each writer receives an
// already-narrowed frame; the caller owns the type discrimination.
type PnlSnapshotSseEvent = ReturnType<typeof PnlSnapshotSseEventSchema.parse>;
// `usePnlSnapshot` appends `hubUser?.defiAddress` as a 5th key element, and
// `setQueryData` is exact-match only — writers use `setQueriesData` with a prefix.
type PnlSnapshotQueryKey = ReturnType<typeof getPnlSnapshotQueryKey>;

const applyPnlSnapshot = (
  queryClient: QueryClient,
  event: Extract<PnlSnapshotSseEvent, { type: typeof PnlSnapshotSseEventType.PNL_SNAPSHOT }>,
  queryKey: PnlSnapshotQueryKey,
  scope: { campaignName: string; epochId: number; exchangeId?: string },
) => {
  // If the initial query was still in-flight, prev is undefined and the update
  // is dropped — with staleTime: Infinity the query never auto-refetches, so
  // invalidate to force a fetch that includes the new snapshot.
  let cacheHit = false;
  queryClient.setQueriesData<PnlSnapshotQueryData>({ queryKey }, (prev) => {
    if (!prev) return prev;
    cacheHit = true;
    let nextState = prev.hydrationState;
    for (const newUser of requireFullUserSnapshots(event.payload.users)) {
      for (const snapshot of newUser.snapshots) {
        nextState = applyTradeSnapshotReducer(nextState, snapshot, newUser.defiAddress, newUser.nickname);
      }
    }
    return { ...prev, hydrationState: nextState };
  });
  if (!cacheHit) {
    invalidatePnlSnapshot(queryClient, scope);
  }
};

const applyPriceEvent = (
  queryClient: QueryClient,
  event: Extract<PnlSnapshotSseEvent, { type: typeof PnlSnapshotSseEventType.PRICE_EVENT }>,
  queryKey: PnlSnapshotQueryKey,
) => {
  queryClient.setQueriesData<PnlSnapshotQueryData>({ queryKey }, (prev) => {
    if (!prev) return prev;
    const nextState = applyPriceEventReducer(prev.hydrationState, event.payload.priceEvent);
    return { ...prev, hydrationState: nextState };
  });
};
